/**
 * Worker pipeline panel types.
 *
 * The backend owns all business logic: ordering, statuses, labels, tooltips,
 * and available actions. The frontend renders the response verbatim and
 * derives all visual styling (badge colors, button variants) from the
 * PipelineStepStatus / PipelineActionType enums below — the backend never
 * returns colors or component variant names.
 *
 * Monorepo rule: backend sends state, frontend renders.
 *
 * Adding a new pipeline step, status, or action is a backend-only change;
 * the frontend picks up new labels and tooltips without code changes, and
 * new enum members only need wiring in the variant mapper + dispatcher.
 */

/**
 * All action types the pipeline endpoint can emit. This is the single coupling
 * point between backend and frontend: the frontend's action dispatcher has a
 * switch on this enum that routes each action to an existing mutation hook or
 * navigation call. Adding a new action type = add one enum member + one case
 * in the dispatcher + have a builder emit it.
 */
import { ApplicationResultStatus } from './worker-applications'
import { RequirementType } from './requirements'

export enum PipelineActionType {
  // Recruiter phone interview - set qualification status
  SET_QUALIFICATION_QUALIFIED = 'SET_QUALIFICATION_QUALIFIED',
  SET_QUALIFICATION_DISQUALIFIED = 'SET_QUALIFICATION_DISQUALIFIED',
  SET_QUALIFICATION_CALL_AGAIN = 'SET_QUALIFICATION_CALL_AGAIN',
  SET_QUALIFICATION_MAYBE = 'SET_QUALIFICATION_MAYBE',
  SET_QUALIFICATION_FOLLOW_UP = 'SET_QUALIFICATION_FOLLOW_UP',
  // Recruiter phone interview - clear qualification (reset in case of misclick)
  CLEAR_QUALIFICATION = 'CLEAR_QUALIFICATION',

  // AI vetting call
  RESEND_AI_CALL = 'RESEND_AI_CALL',

  // Generic application item action (background check, etc.)
  MARK_APP_ITEM_COMPLETE = 'MARK_APP_ITEM_COMPLETE',

  // Requirement actions
  APPROVE_REQUIREMENT = 'APPROVE_REQUIREMENT',
  WAIVE_REQUIREMENT = 'WAIVE_REQUIREMENT',
  REJECT_REQUIREMENT = 'REJECT_REQUIREMENT',
  REVERSE_REQUIREMENT = 'REVERSE_REQUIREMENT',
  RESET_REQUIREMENT = 'RESET_REQUIREMENT',

  // Schedule transitions
  ADD_TO_SCHEDULE = 'ADD_TO_SCHEDULE',
  GO_TO_SCHEDULE = 'GO_TO_SCHEDULE',

  // Background check push/lock actions
  PUSH_BGC_IN_APP = 'PUSH_BGC_IN_APP',
  PUSH_AND_LOCK_BGC = 'PUSH_AND_LOCK_BGC',
  LIFT_BGC_ACTION = 'LIFT_BGC_ACTION',

  // Background check review decisions (when Checkr returns 'review' or 'pre-adverse')
  APPROVE_BGC = 'APPROVE_BGC',
  DENY_BGC = 'DENY_BGC',

  // Push in-app onboarding content (orientation videos, quizzes, forms) to the
  // worker. Surfaces the orientation in the worker app without requiring shift
  // assignment.
  PUSH_ONBOARDING = 'PUSH_ONBOARDING',

  // Pipeline-driven lab DT post-waiver gate (ENG-19041): release the vendor
  // barcode/QR to the worker after ops review.
  APPROVE_DRUG_TEST_FOR_BARCODE = 'APPROVE_DRUG_TEST_FOR_BARCODE',
}

export const IN_APP_ONBOARDING_REQUIREMENT_TYPES: ReadonlySet<RequirementType> =
  new Set([
    RequirementType.ASYNC_VIDEO,
    RequirementType.ONBOARDING_QUIZ,
    RequirementType.ONBOARDING_FORM,
    RequirementType.ORIENTATION,
    RequirementType.VIRTUAL_TRAINING_SESSION,
  ])

export const BACKGROUND_CHECK_STEP_ID = 'background-check'

/**
 * A semantic status for a pipeline step. The backend picks the right one and
 * the frontend derives both the badge color and the current-step highlight
 * from it.
 */
export enum PipelineStepStatus {
  NOT_STARTED = 'NOT_STARTED',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  WAIVED = 'WAIVED',
  BLOCKED = 'BLOCKED',
  REJECTED = 'REJECTED',
  NOT_APPLICABLE = 'NOT_APPLICABLE',
}

/**
 * A key/value row shown in a step's expanded details panel.
 */
export interface PipelineDetailSection {
  label: string
  value: string
}

/**
 * An action button the user can click on a pipeline step.
 *
 * - `type` drives both the frontend dispatcher and the button visual style.
 * - `label`, `disabled`, `disabledReason`, `confirmMessage`, `confirmInputLabel`
 *    are authored by the backend for rendering.
 * - `payload` is opaque structured data the dispatcher reads based on `type`.
 */
export interface PipelineAction {
  type: PipelineActionType
  label: string
  disabled: boolean
  disabledReason?: string
  /** If set, the frontend shows a confirm modal with this message before firing */
  confirmMessage?: string
  /** If set, the confirm modal also includes a text input with this label (e.g. "Waive reason") */
  confirmInputLabel?: string
  /** Opaque payload; only the dispatcher case for this action.type reads from it */
  payload: Record<string, string | number | boolean | null>
}

/**
 * One step in the pipeline. The backend guarantees `steps` in the response are
 * already in display order — the frontend does not reorder.
 */
export interface PipelineStep {
  /** Stable opaque id: e.g. 'ai-vetting-call', 'req:<uuid>', 'onboarding' */
  id: string
  /** Display number (1..N) for the round step indicator */
  stepNumber: number
  /** Display label, e.g. "AI Vetting Call", "W2 Onboarding", requirement name */
  label: string
  /** Semantic status; frontend uses this for both highlighting and color */
  status: PipelineStepStatus
  /** Short status name shown inside the badge, e.g. "Qualified", "Completed" */
  statusLabel: string
  /** Long-form tooltip explaining the current status */
  tooltip: string
  bgcRaw?: {
    status: string | null
    adjudication: string | null
    assessment: string | null
    progressiveState: string | null
  } | null
  bgcActiveAction?: {
    actionType: 'BLOCKED_ALL' | 'DISMISSIBLE'
  } | null
  detailSections: PipelineDetailSection[]
  actions: PipelineAction[]
  /** True for the single "current" step in the pipeline (the one ops should act on next) */
  isCurrent: boolean
  requirementType?: RequirementType
  /**
   * Per-step memo. Only set on requirement steps (`id` starts with `req:`),
   * sourced from `WorkerRequirement.metadata.memo`. Frontend opens the
   * existing `MemoModal` when present or empty to edit it.
   */
  memo?: string
  /** Worker-requirement id used to PATCH the memo. Only set when a record exists. */
  memoWorkerRequirementId?: string
  /**
   * Source requirement id, used to upsert a memo when no WorkerRequirement
   * record exists yet via PATCH `/requirements/worker/upsert/memo`.
   */
  memoRequirementId?: string
}

/**
 * The top-of-tab summary badge + progress count. `headlineStatus` drives the
 * badge color; the frontend derives it via `toBadgeVariant`.
 */
export interface PipelineSummary {
  currentStepId: string | null
  completedCount: number
  totalCount: number
  /** Overall pipeline status — the frontend derives the badge color from this */
  headlineStatus: PipelineStepStatus
  headlineLabel: string
  headlineTooltip: string
  isShiftReady: boolean
  isOnSchedule: boolean
  /**
   * Set when the application is inactive (operator withdrew / dropped off, or
   * archived/canceled). The frontend renders a neutral badge + this state's
   * label, overriding `headlineStatus`. Null/undefined for active applications.
   */
  inactiveResult?: ApplicationResultStatus | null
}

/**
 * Full response body of POST /worker-pipeline/get.
 */
export interface WorkerPipelineResponse {
  /** The real worker this pipeline represents, or null for ghost profiles. */
  workerId: string | null
  /** The ghost profile id when the subject has not converted to a worker yet. */
  ghostProfileId: string | null
  scheduleId: string
  scheduleName: string
  /** The single WorkerApplication we used for status; null when no application exists */
  canonicalApplicationId: string | null
  /** All applications we considered while picking the canonical one (for debugging / future picker UI) */
  applicationIdsConsidered: string[]
  summary: PipelineSummary
  steps: PipelineStep[]
}

/**
 * Request body for POST /worker-pipeline/get.
 *
 * - Exactly one of `workerId` / `ghostProfileId` must be provided. Real workers
 *    pass `workerId`; ghost profiles (not yet linked to a worker account) pass
 *    `ghostProfileId` — in that case the pipeline still renders every step but
 *    every post-ATS step falls back to BLOCKED pending account conversion.
 * - `scheduleId` must be a ShiftRequestParent id (a "schedule" in user terms).
 * - `applicationIds` is optional: if provided, the backend prefers the one whose
 *    application entity matches the schedule; if none match it falls back to the
 *    newest in the list. If not provided, the backend auto-discovers via the
 *    schedule's shift requests.
 */
export interface GetWorkerPipelineRequest {
  workerId?: string
  ghostProfileId?: string
  scheduleId: string
  applicationIds?: string[]
}

/**
 * Request body for POST /worker-pipeline/search.
 *
 * Returns a paginated list of worker pipeline rows for the Ops Control Center.
 * The backend resolves applications, builds the full pipeline for each worker,
 * and applies optional pipeline step + status filters.
 */
/**
 * Sentinel value for the OCC target-schedule filter. Selects workers who have
 * no tag association in the `occ:target-schedule:<companyId>` namespace.
 */
export const TARGET_SCHEDULE_UNASSIGNED = '__UNASSIGNED__'

export interface SearchWorkerPipelinesRequest {
  scheduleId: string
  /**
   * Pipeline the OCC view is scoped to (pipeline picker). Only affects the
   * `slots` KPI: when set, the slots aggregate covers the shift requests of
   * every schedule the pipeline is attached to instead of just `scheduleId`.
   */
  pipelineId?: string
  applicationIds?: string[]
  pipelineStepId?: string
  /**
   * Filter to multiple pipeline steps at once (OR semantics). When set,
   * takes precedence over `pipelineStepId`. Workers matching ANY of the
   * listed steps are returned.
   */
  pipelineStepIds?: string[]
  /**
   * When a pipeline step filter is active, narrows to workers whose status at
   * that step equals this value. When no step filter is set, only `REJECTED`
   * is supported and filters to workers with any REJECTED step in their
   * pipeline (step-agnostic rejection filter).
   */
  pipelineStepStatus?: PipelineStepStatus
  /**
   * Funnel-stage drill-down: show every worker who has REACHED this funnel
   * stage (settled or currently eligible), matching the stage's cumulative
   * funnel/segment count. Used by the Shift-ready / On-schedule status segments.
   * The value is a funnel stage id (`shift-ready`, `on-schedule`, `req:<uuid>`).
   */
  reachedFunnelStageId?: string
  hideRejected?: boolean
  /**
   * When false/omitted (default), inactive applications (withdrawn / dropped
   * off / archived / canceled) are hidden from the list but remain in the
   * funnel counts and the cumulative summary. Set true to show them in the list.
   */
  includeInactive?: boolean
  /**
   * Isolate the dropped-off cohort: return ONLY inactive applications, each
   * badged at the step they reached. Powers the "Dropped off" status segment
   * (the parallel to `pipelineStepStatus = REJECTED`). Implies the inactive
   * rows are shown. The cumulative summary count is unaffected.
   */
  droppedOffOnly?: boolean
  search?: string
  assignedInternalUserIds?: string[]
  /**
   * Filter to workers whose target schedule matches this value. The value is
   * the tag name in the `occ:target-schedule:<companyId>` namespace — either
   * a real `shiftRequestParentId` or an incoming schedule tag name
   * (`incoming:<uuid>`). Pass `TARGET_SCHEDULE_UNASSIGNED` to match workers
   * who have no target schedule at all.
   */
  targetScheduleId?: string
  page?: number
  pageSize?: number
}

/** Lightweight assigned-user shape returned on each search row. */
export interface AssignedInternalUserInfo {
  id: string
  firstName: string
  lastName: string
  email: string
}

/**
 * One required attribute from the AI vetting call's prompt context, paired with
 * whether the worker actually has it. Mirrors the check/X breakdown shown in the
 * call viewer so the OCC table can surface it on hover.
 */
export interface VettingAttributeStatus {
  /** Attribute (V3) id. */
  id: string
  /** Polished display label (the attribute's displayNameEn). */
  label: string
  /** Whether the worker holds this attribute. */
  has: boolean
}

/**
 * One row in the paginated worker-pipeline search results.
 *
 * Contains the full pipeline response plus lightweight worker metadata
 * that the table needs for display without extra round-trips.
 */
export interface WorkerPipelineSearchRow {
  pipeline: WorkerPipelineResponse
  workerName: string
  phoneNumber: string
  photoUrl: string | null
  isGhost: boolean
  appliedAt: string
  accountApprovalStatus: string | null
  aiScore: number | null
  aiQualified: boolean | null
  /**
   * Required attributes from the latest scored AI vetting call's prompt context,
   * each flagged with whether the worker has it. Empty when there is no scored
   * call or the call's prompt context declared no required attributes.
   */
  vettingAttributes: VettingAttributeStatus[]
  /** WorkerApplication.qualificationStatus from the canonical application (e.g. OPS_QUALIFIED). */
  qualificationStatus: string | null
  assignedInternalUser: AssignedInternalUserInfo | null
  memoLastEditedBy?: string
  memo?: string
  /**
   * Id of the worker_requirement whose memo is surfaced in `memo`. Needed so
   * the UI can edit the memo even when the worker's current pipeline step is
   * an ATS step.
   */
  memoRequirementId?: string
  opsNotes?: string
}

/** Aggregated status counts across all filtered workers (not just the page). */
export interface PipelineSearchSummary {
  total: number
  inProgress: number
  shiftReady: number
  rejected: number
  scheduled: number
  /** Inactive applications (withdrawn/stale/archived/canceled) shown in the list. */
  droppedOff: number
}

/**
 * Shift-request fill across the schedules the search is scoped to (the
 * pipeline's schedules when a pipeline scope is set, else the selected
 * schedule): SUM(slotsFilled) / SUM(slotsRequested) over non-cancelled
 * shifts. Returned alongside `summary` on POST /worker-pipeline/search.
 */
export interface PipelineSearchSlots {
  filled: number
  requested: number
}

/**
 * Per-stage status breakdown for stacked funnel bars. Total reaching the stage =
 * rejected + inProgress + completed + droppedOff. Only emitted for stages that
 * benefit from a status split — Shift Ready and On Schedule are terminal "made
 * it" buckets and stay as single-count bars on the frontend.
 *
 * `droppedOff` is the inactive (withdrawn / stale / archived / canceled) slice
 * of what would otherwise be "in progress" at the stage: those workers reached
 * the stage but are no longer active, so they're split out of `inProgress`
 * (which stays active-only, matching the active-only step drill-down) into
 * their own bucket. The total reaching the stage is unchanged.
 */
export interface FunnelStageStatusCounts {
  rejected: number
  inProgress: number
  completed: number
  droppedOff: number
}

/**
 * Request body for POST /worker-pipeline/funnel-timings.
 * Mirrors cohort filters of /search minus pagination / per-step filters.
 */
export interface FunnelTimingsRequest {
  scheduleId: string
  applicationIds?: string[]
  search?: string
  assignedInternalUserIds?: string[]
  appliedAfter?: string
}

/**
 * Percentile + mean aggregate for a single stage-to-stage transition. All
 * durations are in seconds; null when `count === 0`.
 */
export interface TransitionMetrics {
  p25: number | null
  p50: number | null
  p75: number | null
  p90: number | null
  mean: number | null
  count: number
}

export interface TransitionCensoredMetrics {
  count: number
  meanCurrentWaitSeconds: number | null
}

export interface FunnelTransitionTimings {
  fromStepId: string
  toStepId: string
  current: {
    completed: TransitionMetrics
    censored: TransitionCensoredMetrics
    excluded: number
  }
  previous: {
    completed: TransitionMetrics
  }
  history: Array<{
    weekStart: string
    p50: number | null
    p75: number | null
    p90: number | null
    mean: number | null
    count: number
  }>
}

export interface FunnelTimingsResponse {
  transitions: FunnelTransitionTimings[]
}
